import { PaperClipOutlined } from '@ant-design/icons'
import { Flex, Table, Typography } from 'antd'
import { ColumnsType } from 'antd/es/table'
import { DatePicker } from 'antd/lib'
import dayjs from 'dayjs'
import { useState } from 'react'
import { Link } from 'react-router-dom'

import { useInvestigationsServiceGetInvestigationDemandListEndpoint } from 'src/api/queries'
import {
  InvestigationDemandListItemDto,
  InvestigationDemandStatus,
  NewbornInvestigationPeriod,
  PregnancyPeriod
} from 'src/api/requests'
import { Select, Text } from 'src/components'
import {
  NewBornPeriodStatusTag,
  PregnancyPeriodStatusTag
} from 'src/features/persons/components'
import { getFullName } from 'src/features/users/utils'
import {
  useTablePagination,
  useUser,
  useDefaultSortingItem,
  onDownloadFile
} from 'src/hooks'
import {
  dayMonthYearFormatWithDot,
  dayMonthYearHourMinutesFormatWithDot
} from 'src/libs'
import { useLocales } from 'src/locales'
import { paths } from 'src/paths'
import { resetPagination } from 'src/utils'

export const NewbornDemandsTab = () => {
  const { t } = useLocales()
  const { userData } = useUser()
  const { currentLang } = useLocales()

  const pagination = useTablePagination()

  const [date, setDate] = useState<string[]>([])
  const [status, setStatus] = useState('')

  const { defaultSortingOption } = useDefaultSortingItem()

  const queryParams = {
    pageNumber: pagination.current ?? 1,
    pageSize: pagination.pageSize ?? 10,
    institutionId: String(userData?.currentLegalEntityId),
    ...(status && {
      status: status as InvestigationDemandStatus
    }),
    isNewborn: true,
    ...(date?.[0] &&
      date?.[1] && {
        createdAtFrom: date?.[0],
        createdAtTo: date?.[1]
      })
  }

  const { data, isLoading } =
    useInvestigationsServiceGetInvestigationDemandListEndpoint({
      ...queryParams
    })

  const onStatusSelect = (val: string) => {
    setStatus(val)
    resetPagination(pagination)
  }

  const investigationsStatusOptions = [
    defaultSortingOption,
    ...Object.values(InvestigationDemandStatus).map((item) => ({
      value: item,
      label: t(`tests.statuses.${item}`)
    }))
  ]

  const columns: ColumnsType<InvestigationDemandListItemDto> = [
    {
      key: 'investigation number',
      title: t('investigations.investigation_number'),
      dataIndex: 'code'
    },
    {
      key: 'child',
      title: t('pregnancy.newborn_investigation_status.child'),
      render: (_, { person }) => {
        const name = getFullName(
          person?.firstName ?? undefined,
          person?.lastName ?? undefined
        )

        return person?.id ? (
          <Link to={paths.persons.details(person.id)}>{name || '-'}</Link>
        ) : (
          name || '-'
        )
      }
    },
    {
      key: 'created date',
      title: t('investigations.created_date'),
      render: ({ createdAt }) =>
        dayjs.utc(createdAt).tz().format(dayMonthYearHourMinutesFormatWithDot)
    },
    {
      key: 'pregnancyPeriod',
      title: t('pregnancy.monitoring_period'),
      render: (record: InvestigationDemandListItemDto) => (
        <PregnancyPeriodStatusTag
          status={record?.pregnancyPeriod ?? PregnancyPeriod.INITIAL}
        />
      )
    },
    {
      key: 'newbornPeriod',
      title: t('consultations.harvest_date'),
      render: (_, { newbornPeriod }) => (
        <NewBornPeriodStatusTag
          status={
            newbornPeriod ?? NewbornInvestigationPeriod.AT_FORTY_EIGHT_HOURS
          }
        />
      )
    },
    {
      key: 'documents',
      title: t('stock_applications.uploaded_documents'),
      render: (_, { results }) => {
        const mediaFiles = results?.flatMap((r) => r.mediaItems ?? []) ?? []

        if (mediaFiles.length === 0) return '-'

        return (
          <Flex vertical gap={2}>
            {mediaFiles.map((item) => (
              <Typography.Link
                key={item.id}
                className="fit-content"
                onClick={() => onDownloadFile(t, item.id, item.name)}
              >
                <Flex gap={4}>
                  <PaperClipOutlined />
                  {item.name}
                </Flex>
              </Typography.Link>
            ))}
          </Flex>
        )
      }
    },
    {
      key: 'result',
      title: t('analysis.result'),
      render: (_, { results }) => {
        if (!results || results.length === 0) return '-'

        return (
          <Flex vertical gap={8}>
            {results.map((result, idx) => {
              const {
                resultValue,
                resultOption,
                resultValueOption,
                resultDate
              } = result

              const label =
                resultOption?.translation?.[currentLang?.value] ??
                resultValueOption?.translation?.[currentLang?.value] ??
                (typeof resultValue === 'number' ? resultValue : null)

              const formattedDate = resultDate
                ? dayjs
                    .utc(resultDate)
                    .tz()
                    .format(dayMonthYearHourMinutesFormatWithDot)
                : null

              return (
                <div key={idx}>
                  <div>{label ?? '-'}</div>
                  {formattedDate && (
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      {formattedDate}
                    </Text>
                  )}
                </div>
              )
            })}
          </Flex>
        )
      }
    },
    {
      key: 'referral doctor',
      title: t('investigations.referral_doctor'),
      render: (_, { referralDoctorName, referralDoctorUser }) =>
        referralDoctorName ||
        getFullName(
          referralDoctorUser?.firstName,
          referralDoctorUser?.lastName
        ) ||
        '-'
    }
  ]

  return (
    <Flex vertical gap={16}>
      <Flex gap={8}>
        <DatePicker.RangePicker
          format={dayMonthYearFormatWithDot}
          onChange={(_, dateStrings) => setDate(dateStrings)}
          placeholder={[t('specimens.created_by'), t('specimens.created_at')]}
        />
        <Select
          onSelect={onStatusSelect}
          options={investigationsStatusOptions}
          placeholder={t('form.status')}
        />
      </Flex>
      <Table
        rowKey="id"
        scroll={{ x: 'max-content' }}
        loading={isLoading}
        columns={columns}
        dataSource={data?.data}
        pagination={{ ...pagination, total: data?.totalCount }}
      />
    </Flex>
  )
}
